/**
 * Add-Comment Types
 *
 * Type definitions for the add_comment_tool feature that lets the AI suggest a
 * Word comment (annotation) for user approval. On approval the comment is
 * inserted at the targeted location — scoped to a phrase within the paragraph
 * when an anchorPhrase is given, otherwise on the whole paragraph.
 *
 * Mirrors the auto-redline tool's output/guard conventions (autoRedlineTypes.ts).
 */

/**
 * Complete output from the add_comment tool.
 * This is the structure received from the backend in tool_use_result chunks
 * (after unwrapping the { tool_name, tool_use_id, tool_input, content } envelope).
 */
export interface AddCommentOutput {
  /** Backend section id the comment targets (e.g. "p15" / table-position id) */
  sectionId: string;

  /** Human-readable section name — used in the card header + logging */
  sectionName: string;

  /** 0-based body paragraph index the comment targets */
  paragraphId: number;

  /**
   * Cleaned paragraph text (backend `strip_tags` form: deletions + insertions
   * inline, comment/tag markers removed). Used as the whole-paragraph stale
   * guard — compared against the live `paragraph.text` when there is no
   * anchorPhrase.
   */
  anchorParagraphText: string;

  /**
   * Optional sub-range target: an exact phrase within the section to attach the
   * comment to. null / undefined = flag the whole paragraph.
   */
  anchorPhrase?: string | null;

  /** The comment body text to insert */
  commentText: string;

  /** Original tool_use_id from the Anthropic API (for conversation history reconstruction) */
  tool_use_id?: string;

  /** The model's original tool input parameters (for conversation history reconstruction) */
  tool_input?: Record<string, unknown>;
}

/**
 * Status states for a single add-comment card.
 *
 * "stale" = the target paragraph/phrase changed since the BE prepared the
 * comment; the apply was blocked (distinct from "failed" = the Word insert
 * itself errored).
 */
export type CommentApplyStatus = "pending" | "applying" | "applied" | "failed" | "stale";

/**
 * Type guard to check if an object is an AddCommentOutput.
 * Guard pattern mirrors isAutoRedlineOutput (autoRedlineTypes.ts).
 */
export function isAddCommentOutput(obj: unknown): obj is AddCommentOutput {
  if (!obj || typeof obj !== "object") {
    return false;
  }

  const candidate = obj as Record<string, unknown>;

  // Required string fields
  if (
    typeof candidate.sectionId !== "string" ||
    typeof candidate.sectionName !== "string" ||
    typeof candidate.anchorParagraphText !== "string" ||
    typeof candidate.commentText !== "string"
  ) {
    return false;
  }

  // paragraphId is required and must be a non-negative number
  if (typeof candidate.paragraphId !== "number" || candidate.paragraphId < 0) {
    return false;
  }

  // anchorPhrase is optional (string | null)
  if (
    candidate.anchorPhrase !== undefined &&
    candidate.anchorPhrase !== null &&
    typeof candidate.anchorPhrase !== "string"
  ) {
    return false;
  }

  return true;
}
